<template>
    <div class="settings-panels">
        <div class="sub-panel-headers" :class="{ 'hidden-panel': anyExpanded, 'visible-panel': !anyExpanded }">
            <div v-for="(setting, panelId) in panels" :key="panelId">
                <button class="sub-setting-heading" @click="toggleExpand(panelId)">
                    {{setting.label}}
                    <span class="expander-toggle">
                        <span class="sr-only">closed</span>
                        <span class="e-icons e-slide-open"></span>
                    </span>
                </button>
            </div>
        </div>
        <div v-for="(setting, panelId) in panels" :key="panelId">
            <div class="sub-setting-outer">
                <div class="sub-setting-panel" :class="{ 'visible-panel': expanded[panelId], 'hidden-panel':!expanded[panelId]  ,'clip-content': !showContent[panelId] }">
                    <button class="sub-setting-back" @click="toggleExpand(panelId)">
                        <span class="expander-toggle">
                            <span class="sr-only">opened</span>
                            <span class="e-icons e-slide-close"></span>
                        </span>
                        {{setting.label}}
                    </button>
                    <div class="sub-setting-contents" v-if="expanded[panelId]">
                        <div v-for="(cFields, cKey) in panelColumns[panelId]" :key="cKey" class="settings-column">
                            <div v-for="(field, fieldIndex) in cFields" :key="fieldIndex">
                                <div v-if="field.config.type === 'COMPONENT'">
                                    <label-wrapper :label-settings="field.config"></label-wrapper>
                                    <component :is="field.config.cType" :cId="field.config.cId + '__' + field.config.id" :prop-component-id="componentId"></component>
                                </div>
                                <div v-else>
                                    <control
                                        v-if="field.parentKey"
                                        :name="field.key"
                                        :prop-config="field.config"
                                        :values-map="valuesMap"
                                        :disabled="isLocked"
                                        :value="valuesMap[field.parentKey][0][field.key]"
                                        index="0"
                                        @update:value="valuesMap[field.parentKey][0][field.key] = $event"
                                        :recordId="componentId"
                                    />
                                    <control
                                        v-else
                                        :name="field.key"
                                        :disabled="isLocked"
                                        :prop-config="field.config"
                                        :values-map="valuesMap"
                                        :value="valuesMap[field.key]"
                                        @update:value="valuesMap[field.key] = $event"
                                        :recordId="componentId"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import labelWrapper from "@common/control/labelWrapper";
import { defineAsyncComponent } from "@common/defineAsyncComponent";

let editorSettings = {};
const controlComponent = defineAsyncComponent(() => import('@common/control/control.vue'));
export const settingsPanel = {
    name: 'settingsPanels',
    editorSettings: editorSettings,
    props: {
        componentId: {
            type: String
        },
        settingsMap: {
            type: Object
        },
        valuesMap: {
            type: Object
        },
        isLocked: {
            type: Boolean,
            default: false
        },
        subPanelColumns: {
            type: Number,
            default: 1
        },
        showMaxColumns: {
            type: Boolean,
            default: true
        },
        openedPanel: {
            type: String,
            default: false
        }
    },
    data() {
        return {
            expanded: {},
            showContent: {},
            anyExpanded: false,
            windowHeight: window.innerHeight,
            windowWidth: window.innerWidth
        };
    },
    components: {
        control: controlComponent,
        labelWrapper: labelWrapper
    },
    mounted() {
        if (this.openedPanel) {
            this.expanded[this.openedPanel] = true;
            this.showContent[this.openedPanel] = true;
            this.anyExpanded = true;
        }
    },
    computed: {
        currentColumnCount() {
            let count = 1;
            if (this.anyExpanded) {
                let maxColumns = Math.floor(this.windowWidth / 560); // NOSONAR
                if (!this.showMaxColumns) maxColumns = 1;
                for (let panel in this.expanded) {
                    if (panel === 'length') continue;
                    if (!this.expanded[panel]) continue;
                    for (let pKey in this.panels[panel].fields) {
                        if (pKey === 'length') continue;
                        let field = this.panels[panel].fields[pKey];
                        let sColumn = this.settingsMap[field] && this.settingsMap[field].column;
                        if ( // NOSONAR
                            sColumn
                            // && sColumn <= maxColumns
                            && count < sColumn
                        ) {
                            count = sColumn;
                        }
                    }
                }
                this.$emit('update:subPanelColCount', count);
                count = Math.min(maxColumns, count);
            }
            if (this.subPanelColumns !== count) this.$emit('update:subPanelColumns', count);
            return count;
        },
        panelColumns() {
            let output = {};
            let setParentValue = (parentKey) => {
                let newItem = library.cloneObj(this.settingsMap[parentKey].itemConfig.newItem);
                if (!Array.isArray(this.valuesMap[parentKey])) this.valuesMap[parentKey] = [];
                if (!this.valuesMap[parentKey].length) this.valuesMap[parentKey].push(newItem);
            };
            for (let panel in this.panels) {
                if (panel === 'length') continue;
                for (let pKey in this.panels[panel].fields) {
                    if (pKey === 'length') continue;
                    let parentKey;
                    let subKey;
                    let fullKey = this.panels[panel].fields[pKey];
                    let kParts = this.panels[panel].fields[pKey].split('.');
                    if (kParts.length > 1) {
                        parentKey = kParts[0];
                        subKey = kParts[kParts.length - 1];
                    } else {
                        parentKey = kParts[0];
                    }
                    let sColumn = this.settingsMap[parentKey] && this.settingsMap[parentKey].column;
                    if (!sColumn) continue;
                    let cColumn = sColumn <= this.currentColumnCount ? sColumn : this.currentColumnCount;
                    if (!output[panel]) output[panel] = {};
                    if (!output[panel]['column' + cColumn]) output[panel]['column' + cColumn] = {};
                    if (subKey) {
                        // setup the default value
                        setParentValue(parentKey);
                        output[panel]['column' + cColumn][fullKey] = {
                            key: subKey,
                            parentKey,
                            config: this.settingsMap[parentKey].itemConfig.keys[subKey]
                        };
                    }
                    else {
                        output[panel]['column' + cColumn][parentKey] = {
                            key: parentKey,
                            config: this.settingsMap[parentKey]
                        };
                    }
                }
            }
            return output;
        },
        settingsEditorWidth() {
            return this.currentColumnCount * 560; // NOSONAR
        },
        panels() {
            let output = {};
            let keys = Object.keys(this.settingsMap);
            // library.logicParser.call(this, panel.logic, panel)
            keys.forEach((key) => {
                const panel = this.settingsMap[key];
                if (panel.type === 'panel') {
                    library.logicParser.call(this, panel.logic, panel);
                    if (panel.display !== false) output[key] = this.settingsMap[key];
                }
            });
            return output;
        },
        fieldKeys() {
            let panelKeys = Object.keys(this.panels);
            let keys = [];
            panelKeys.forEach((pKey) => {
                keys = keys.concat(this.panels[pKey].fields);
            });
            return keys;
        },
    },
    methods: {
        toggleExpand(panelId) {
            // Reset all other panels to false
            let keys = Object.keys(this.expanded);
            keys.forEach((key) => {
                if (key !== panelId) this.expanded[key] = false;
            });
            // Set the correct expanded panel
            this.expanded[panelId] = this.expanded[panelId] !== true;
            // Show the content (prevents scrollbars when collapsed)
            if (!this.showContent[panelId]) this.showContent[panelId] = true;
            else {
                // Adjust the timeout time 300 for closing, 100 for opening
                let timeoutTime = this.showContent[panelId] ? 300 : 100;
                setTimeout(() => {
                    this.showContent[panelId] = this.showContent[panelId] !== true;
                    this.$forceUpdate();
                }, timeoutTime); // NOSONAR
            }
            // Set anyExpanded to true or false
            this.anyExpanded = this.expanded[panelId];
            let tmpOpenedPanel = false;
            if (this.expanded[panelId]) tmpOpenedPanel = panelId;
            this.$emit('update:openedPanel', tmpOpenedPanel);
            this.$forceUpdate();
        }
    },
    updated() {
        // TODO make sure this is working
        // if (typeof this.$parent.component.updateLockedControls === 'function') {
        //     this.$parent.component.updateLockedControls();
        // }
        for (let pKey in this.panels) {
            if (pKey === 'length') continue;
            let panel = this.panels[pKey];
            if (panel.logic) {
                library.logicParser.call(this, panel.logic, panel);
            }
        }
    }
};

export default settingsPanel;
</script>

<style lang="css" scoped>
    .settings-panels {
        overflow-x: hidden;
        position: relative;
        width: 100%;
        height: 100%;
    }
    .sub-setting-outer {
        height: 0;
    }
    .sub-setting-panel {
        position: relative;
        overflow-y: visible;
        right: -1000px;
        width: 100%;
    }
    .sub-panel-headers {
        position: relative;
        left: 0;
        width: 100%;
        height: 0;
        overflow-y: visible;
    }
    .settings-panels .sub-setting-heading, .settings-panels .sub-setting-back {
        display: block;
        width: 100%;
        text-align: left;
        font-size: 20px;
        padding: 10px;
        background: #e7eef1;
        border: 0 none;
        border-bottom: 1px solid #aaa;
    }
    .settings-panels .sub-setting-back {
        background: #e7eef1;
        position: -webkit-sticky;
        position: sticky;
        top: 0;
        z-index: 1;
    }
    .sub-setting-contents {
        /*padding: 10px;*/
        /*width: 580px;*/
        /*overflow-y: auto;*/
        display: flex;
    }
    .settings-column {
        flex: 1;
        padding: 5px;
    }
    .sub-setting-panel.visible-panel {
        transition: .3s;
        transition-timing-function: ease-out;
        right: 0;
    }
    .sub-setting-panel.hidden-panel {
        transition: .3s;
        transition-timing-function: ease-in;
        right: -100%;
    }
    .sub-panel-headers.visible-panel {
        transition: .3s;
        transition-timing-function: ease-in;
        left: 0;
    }
    .sub-panel-headers.hidden-panel {
        transition: .3s;
        transition-timing-function: ease-out;
        left: -100%;
    }
    .sub-setting-panel.clip-content {
        height: 0;
        overflow: hidden;
    }
    .sub-setting-array {
        background: #eee;
        border: 3px solid white;
        padding: 5px;
    }
    .sub-setting-array:hover {
        background: #fff;
        border: 3px solid lightgreen;
    }
    /* Expand/Collaps Icon */
    .e-slide-close:before {
        content: '\e654' !important;
    }
    .e-slide-open:before {
        content: '\e653' !important;
    }
    .expander-toggle {
        margin-right: 5px;
        opacity: .7;
    }

    .sub-setting-heading:hover .expander-toggle, .sub-setting-back:hover .expander-toggle {
        opacity: 1;
    }
    .expander-toggle .e-collapse {
        position: relative;
        top: 2px;
        left: -2px;
    }
    .expander-toggle .e-expand {
        position: relative;
        top: 2px;
    }
</style>
